import React, { useState, useEffect } from "react";

import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { frontloadConnect } from "react-frontload";
import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet";

import { getBanner } from "./../../../store/banner.reducers";

import api from "./../../../services/api";
import { maskChaveSeguranca, maskCpfCnpj, getDateByTimeZoneCba } from "./../../../services/functions";

import Header from "./../../../components/Header";
import Title from "./../../../components/Title";
import Newsletter from "./../../../components/Newsletter";
import Footer from "./../../../components/Footer";
import MainServices from "./../../../components/MainServices";

import {
  Main,
  Container,
  Content,
  Aside,
  ContentBody,
  ContentMain,
  ContentBox,
  ContentSummary,
  Form,
  GroupInput,
  InputLabel,
  Input,
  BorderInput,
  FormButton,
  FormTitle,
} from "./../Auth/styles";

const frontload = async props => {
  return;
};

function Validate() {
  const recaptchaRef = React.useRef();
  const { key: keyParam } = useParams();
  const [activeInput, setActiveInput] = useState("key");
  const [borderInputPosition, setBorderInputPosition] = useState(0);
  const [loading, setLoading] = useState(false);

  const [key, setKey] = useState(keyParam || "");
  const [cpf, setCpf] = useState("");

  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [checked, setChecked] = useState(false);

  async function setFocus(index) {
    await setActiveInput(index);
    handleScrollForm();
  }

  function handleScrollForm() {
    if (typeof global.window !== "undefined") {
      if (global.window.document.querySelector(`.active`)) {
        const offset = global.window.document.querySelector(`.active`).offsetTop;
        setBorderInputPosition(offset - 18);
      }
    }
  }

  const validateCertificate = async () => {
    try {
      const params = [];
      if (key && key.trim()) params.push(`key=${encodeURIComponent(key.trim())}`);
      if (cpf && cpf.trim()) params.push(`cpf=${encodeURIComponent(cpf.trim())}`);

      const { data } = await api.get(`/courses/validate/certificate/?${params.join("&")}`);
      setResult(data);
      setErrorMessage("");
      setChecked(true);
      setLoading(false);
    } catch (error) {
      setResult(null);
      setErrorMessage(
        (error.response && error.response.data && error.response.data.error) ||
          "Não foi possível verificar a autenticidade. Certificado não encontrado.",
      );
      setChecked(true);
      setLoading(false);
    }
  };

  const handleSave = async event => {
    event.preventDefault();

    if (!key.trim() && !cpf.trim()) {
      alert("Informe o código de validação e/ou o CPF/registro.");
      return;
    }

    setLoading(true);
    setChecked(false);
    setResult(null);
    setErrorMessage("");

    const token = await recaptchaRef.current.executeAsync();

    if (!token) {
      alert("Erro! Tente novamente");
      setLoading(false);
      return;
    }

    validateCertificate();
  };

  useEffect(() => {
    if (keyParam) {
      setKey(keyParam);
      setLoading(true);
      (async () => {
        try {
          const { data } = await api.get(`/courses/validate/certificate/?key=${encodeURIComponent(keyParam)}`);
          setResult(data);
          setErrorMessage("");
        } catch (error) {
          setResult(null);
          setErrorMessage(
            (error.response && error.response.data && error.response.data.error) ||
              "Não foi possível verificar a autenticidade. Certificado não encontrado.",
          );
        } finally {
          setChecked(true);
          setLoading(false);
        }
      })();
    }
  }, [keyParam]);

  const renderCertificateCard = item => (
    <GroupInput key={item.key || item.registro}>
      <FormTitle style={{ color: item.present ? "#2e7d32" : "#c62828" }}>
        {item.present ? "Certificado autêntico" : "Inscrição localizada"}
      </FormTitle>
      <ContentSummary>
        {item.message}
        <br />
        <br />
        <strong>Participante:</strong> {item.participant && item.participant.name}
        <br />
        <strong>CPF:</strong> {item.participant && item.participant.cpf}
        <br />
        <strong>Curso:</strong> {item.course && item.course.name}
        <br />
        {item.course && item.course.start_date ? (
          <>
            <strong>Data:</strong> {getDateByTimeZoneCba(item.course.start_date, "dd'/'MM'/'yyyy")}
            <br />
          </>
        ) : null}
        {item.course && item.course.total_hours ? (
          <>
            <strong>Carga horária:</strong> {item.course.total_hours}h
            <br />
          </>
        ) : null}
        {item.course && item.course.local ? (
          <>
            <strong>Local:</strong> {item.course.local}
            <br />
          </>
        ) : null}
        <strong>Código de validação:</strong> {item.key}
        <br />
        <strong>Nº de registro:</strong> {item.registro}
        <br />
        <br />
        Documento emitido pelo Conselho Regional de Odontologia de Mato Grosso (CRO-MT).
      </ContentSummary>
    </GroupInput>
  );

  return (
    <>
      <Helmet>
        <title>Validar Certificado de Curso - {process.env.REACT_APP_TITLE}</title>
        <meta name="description" content="Consulte a autenticidade de certificados de cursos emitidos pelo CRO-MT" />
        <meta property="og:title" content={`Validar Certificado de Curso - ${process.env.REACT_APP_TITLE}`} />
        <meta name="title" content={`Validar Certificado de Curso - ${process.env.REACT_APP_TITLE}`} />
        <meta property="og:description" content="Consulte a autenticidade de certificados de cursos emitidos pelo CRO-MT" />
        <meta property="og:image" content="https://cromt.org.br/logo512.png" />
        <meta property="og:site_name" content="CROMT" />
        <meta property="og:url" content={global.window ? global.window.location : ""} />
      </Helmet>
      <Header />

      <Main id="main">
        <Container>
          <Content>
            <Title title="Validar Certificado de Curso" />
            <ContentMain>
              <Aside>
                <MainServices />
              </Aside>

              <ContentBody>
                <ContentBox>
                  <Form autoComplete="off" onSubmit={handleSave}>
                    <BorderInput style={{ top: borderInputPosition }} />

                    <GroupInput className={activeInput === "key" ? `active` : ""}>
                      <InputLabel htmlFor="key">Código de Validação</InputLabel>
                      <Input
                        id="key"
                        type="text"
                        onFocus={() => setFocus("key")}
                        value={key}
                        onChange={event => setKey(maskChaveSeguranca(event.target))}
                        onKeyUp={event => setKey(maskChaveSeguranca(event.target))}
                        style={{ textTransform: "uppercase" }}
                        placeholder="Ex.: A1B2.C3D4.E5F6.G7H8"
                      />
                    </GroupInput>

                    <GroupInput className={activeInput === "cpf" ? `active` : ""}>
                      <InputLabel htmlFor="cpf">CPF / Nº de registro</InputLabel>
                      <Input
                        id="cpf"
                        type="text"
                        onFocus={() => setFocus("cpf")}
                        value={cpf}
                        onChange={event => setCpf(maskCpfCnpj(event.target))}
                        placeholder="Opcional — CPF do participante"
                      />
                    </GroupInput>

                    <FormButton disabled={loading}>{loading ? "Validando..." : "Validar autenticidade"}</FormButton>

                    {checked && errorMessage ? (
                      <GroupInput>
                        <FormTitle style={{ color: "red" }}>{errorMessage}</FormTitle>
                      </GroupInput>
                    ) : null}

                    {checked && result && result.valid && result.certificates
                      ? result.certificates.map(item => renderCertificateCard(item))
                      : null}

                    {checked && result && result.valid && !result.certificates ? renderCertificateCard(result) : null}

                    <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey="6Le4SfoUAAAAAIPw67Jqbl9xSaIcUsL4e-P2oQ2c" />
                  </Form>
                </ContentBox>
              </ContentBody>
            </ContentMain>
          </Content>
        </Container>
        <Newsletter />
      </Main>
      <Footer />
    </>
  );
}

const mapStateToProps = state => {
  return {
    banner: state.bannerReducers.banner,
  };
};
const mapDispatchToProps = {
  getBanner,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  frontloadConnect(frontload, {
    onMount: true,
    onUpdate: false,
  })(Validate),
);
